import { Menu, MenuItem } from '@mui/material'
import { theme } from '@traba/theme'
import { useRef, useState } from 'react'
import styled from 'styled-components'
import { SvgIcon } from '../SvgIcon'

export interface SleekSelectOption<T extends string = string> {
  value: T
  label: string
  description?: string
  disabled?: boolean
}

export interface SleekSelectProps<T extends string = string> {
  value: T
  options: SleekSelectOption<T>[]
  onChange: (value: T) => void
  placeholder?: string
  size?: 'sm' | 'md'
  disabled?: boolean
  width?: number | string
}

export function SleekSelect<T extends string = string>({
  value,
  options,
  onChange,
  placeholder,
  size = 'sm',
  disabled,
  width,
}: SleekSelectProps<T>) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const selected = options.find((o) => o.value === value)

  return (
    <>
      <Trigger
        ref={triggerRef}
        type="button"
        $size={size}
        $width={width}
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <TriggerLabel $hasValue={Boolean(selected)}>
          {selected?.label ?? placeholder ?? ''}
        </TriggerLabel>
        <SvgIcon name="chevronDown" size={10} color={theme.colors.Grey60} />
      </Trigger>
      <StyledMenu
        anchorEl={triggerRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              marginTop: '4px',
              minWidth: triggerRef.current?.offsetWidth ?? 'auto',
              borderRadius: `${theme.border.radius}`,
              // Inline literal: business-app theme doesn't yet expose shadow tokens.
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              background: theme.colors.White,
              overflow: 'hidden',
            },
          },
        }}
      >
        {options.map((opt) => {
          const isSelected = opt.value === value
          return (
            <StyledMenuItem
              key={opt.value}
              disabled={opt.disabled}
              $selected={isSelected}
              onClick={() => {
                if (opt.value !== value) {
                  onChange(opt.value)
                }
                setOpen(false)
              }}
            >
              <ItemTextCol>
                <ItemLabel $selected={isSelected}>{opt.label}</ItemLabel>
                {opt.description && <ItemDesc>{opt.description}</ItemDesc>}
              </ItemTextCol>
              {isSelected && (
                <SvgIcon name="check" size={12} color={theme.colors.Violet60} />
              )}
            </StyledMenuItem>
          )
        })}
      </StyledMenu>
    </>
  )
}

const Trigger = styled.button<{ $size: 'sm' | 'md'; $width?: number | string }>`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.space.xxs}px;
  font-family: inherit;
  font-weight: 500;
  color: ${theme.colors.MidnightBlue};
  background: ${theme.colors.White};
  border: 1px solid ${theme.colors.Grey20};
  border-radius: ${theme.border.radius};
  cursor: pointer;
  transition:
    border-color 0.12s ease,
    background-color 0.12s ease,
    box-shadow 0.12s ease;
  width: ${({ $width }) =>
    typeof $width === 'number'
      ? `${$width}px`
      : typeof $width === 'string'
        ? $width
        : 'auto'};

  font-size: ${({ $size }) => ($size === 'sm' ? '11px' : '13px')};
  padding: ${({ $size }) =>
    $size === 'sm' ? '3px 8px 3px 10px' : '6px 12px 6px 14px'};

  &:hover:not(:disabled) {
    border-color: ${theme.colors.Grey30};
    background: ${theme.colors.Grey10};
  }

  &:focus-visible {
    outline: none;
    border-color: ${theme.colors.Violet60};
    box-shadow: 0 0 0 3px ${theme.colors.Violet10};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

const TriggerLabel = styled.span<{ $hasValue: boolean }>`
  color: ${({ $hasValue }) =>
    $hasValue ? theme.colors.MidnightBlue : theme.colors.Grey40};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledMenu = styled(Menu)`
  & .MuiList-root {
    padding: 4px;
  }
`

const StyledMenuItem = styled(MenuItem)<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.space.xs}px;
  padding: 6px 10px;
  border-radius: ${theme.border.radius};
  font-family: inherit;
  font-size: 12px;
  color: ${theme.colors.MidnightBlue};
  background: ${({ $selected }) =>
    $selected ? theme.colors.Violet10 : 'transparent'};

  &:hover {
    background: ${({ $selected }) =>
      $selected ? theme.colors.Violet10 : theme.colors.Grey10};
  }
`

const ItemTextCol = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const ItemLabel = styled.span<{ $selected: boolean }>`
  font-size: 12px;
  font-weight: ${({ $selected }) => ($selected ? 500 : 400)};
  color: ${({ $selected }) =>
    $selected ? theme.colors.Violet80 : theme.colors.MidnightBlue};
`

const ItemDesc = styled.span`
  font-size: 10px;
  color: ${theme.colors.Grey60};
  line-height: 1.4;
`
